import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Settings2, Users2, Lock, Clock, CheckCircle2, Receipt, Code2, Trash2, GraduationCap } from "lucide-react";
import { useI18n } from "@/i18n/i18n";
import { LandingStats } from "@/components/LandingStats";
import { todayISO } from "@/lib/dateFormat";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const hoyISO = todayISO();

const ESTADOS = {
  trial: { k: "lp_st_trial", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  pending_payment: { k: "lp_st_invoiced", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Receipt },
  active: { k: "lp_st_active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  blocked: { k: "lp_st_blocked", cls: "bg-rose-50 text-rose-700 border-rose-200", icon: Lock },
};

export default function LivePass() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [data, setData] = useState({ items: [], summary: {}, total: 0 });
  const [filtro, setFiltro] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [cobro, setCobro] = useState(null);     // {user_id, name, paid_date, payment_method}
  const [borrado, setBorrado] = useState(null); // {user_id, name, keep}

  const cargar = () => {
    api.get("/live-pass/members").then((r) => setData(r.data)).catch(() => {});
    api.get("/live-pass/settings").then((r) => setCfg(r.data)).catch(() => {});
  };
  useEffect(cargar, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put("/live-pass/settings", {
        price: Number(cfg.price),
        trial_days: Number(cfg.trial_days),
        payment_link: cfg.payment_link || "",
        program_name: cfg.program_name,
        currency: cfg.currency,
      });
      setCfg(r.data);
      toast.success(t("lp_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const registrarPago = async () => {
    try {
      const r = await api.post(`/live-pass/${cobro.user_id}/activate`, {
        months: 1, paid_date: cobro.paid_date, payment_method: cobro.payment_method,
      });
      toast.success(t("lp_active_until", { date: r.data.paid_until }));
      setCobro(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar");
    }
  };

  const convertir = async (u) => {
    if (!window.confirm(t("lp_confirm_convert", { name: u.name }))) return;
    try {
      await api.post(`/live-pass/${u.user_id}/convert`);
      toast.success(t("lp_converted"));
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo convertir");
    }
  };

  const borrar = async () => {
    try {
      await api.delete(`/users/${borrado.user_id}`, { params: { keep_history: borrado.keep } });
      toast.success(t("lp_deleted"));
      setBorrado(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    }
  };

  const origen = window.location.origin;
  const snippet = `<iframe src="${origen}/wss-live/embed"
        style="width:100%;max-width:460px;height:620px;border:0"
        title="WSS Live"></iframe>`;

  const items = data.items.filter((u) => {    const q = filtro.trim().toLowerCase();
    if (!q) return true;
    return `${u.name} ${u.email}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="live-pass-admin">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-rose-600" /> {t("live_pass")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t("lp_admin_sub")}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(ESTADOS).map(([k, v]) => (
          <div key={k} className={`border rounded-xl p-4 ${v.cls}`} data-testid={`live-pass-count-${k}`}>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide">
              <v.icon className="w-4 h-4" /> {t(v.k)}
            </div>
            <div className="text-2xl font-bold mt-1">{data.summary?.[k] || 0}</div>
          </div>
        ))}
      </div>

      <LandingStats />

      {cfg && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Settings2 className="w-5 h-5 text-slate-400" /> {t("lp_terms")}
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600">{t("lp_price")}</label>
              <Input
                data-testid="live-pass-price"
                type="number" step="0.01" value={cfg.price}
                onChange={(e) => setCfg({ ...cfg, price: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t("lp_trial_days_label")}</label>
              <Input
                data-testid="live-pass-trial-days"
                type="number" value={cfg.trial_days}
                onChange={(e) => setCfg({ ...cfg, trial_days: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">{t("lp_pay_link")}</label>
              <Input
                data-testid="live-pass-link"
                placeholder="https://..." value={cfg.payment_link || ""}
                onChange={(e) => setCfg({ ...cfg, payment_link: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-slate-500">
              {t("lp_terms_hint")}
            </p>
            <Button data-testid="live-pass-save" onClick={guardar} disabled={guardando}>
              {guardando ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
          <Code2 className="w-5 h-5 text-slate-400" /> {t("lp_embed")}
        </h2>
        <p className="text-sm text-slate-500 mb-3">{t("lp_embed_hint")}</p>
        <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto"
             data-testid="live-pass-embed-code">{snippet}</pre>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="outline" size="sm" data-testid="live-pass-copy-embed"
                  onClick={() => { navigator.clipboard.writeText(snippet); toast.success(t("copied")); }}>
            {t("lp_copy_embed")}
          </Button>
          <a href="/wss-live" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="sm" data-testid="live-pass-open-public">
              {t("lp_open_public")}
            </Button>
          </a>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-3 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users2 className="w-5 h-5 text-slate-400" /> {t("lp_people")} ({data.total})
          </h2>
          <Input
            data-testid="live-pass-search"
            className="max-w-xs" placeholder={t("lp_search")}
            value={filtro} onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            {t("lp_empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">{t("lp_col_person")}</th>
                  <th className="text-left px-4 py-2">{t("lp_col_status")}</th>
                  <th className="text-left px-4 py-2">{t("lp_col_trial_until")}</th>
                  <th className="text-left px-4 py-2">{t("lp_col_paid_until")}</th>
                  <th className="text-right px-4 py-2">{t("lp_col_action")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const lp = u.live_pass || {};
                  const e = ESTADOS[lp.status];
                  return (
                    <tr key={u.user_id} className="border-t border-slate-100" data-testid={`live-pass-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full border ${e ? e.cls : "bg-slate-50 text-slate-600 border-slate-200"}`}>{e ? t(e.k) : (lp.status || "—")}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{lp.trial_ends_on || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{lp.paid_until || "—"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button
                          size="sm" variant="outline"
                          data-testid={`live-pass-activate-${u.user_id}`}
                          onClick={() => setCobro({ user_id: u.user_id, name: u.name,
                                                    paid_date: hoyISO, payment_method: "cash" })}
                        >
                          {t("lp_register_payment")}
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="ml-1"
                          data-testid={`live-pass-convert-${u.user_id}`}
                          onClick={() => convertir(u)}
                        >
                          <GraduationCap className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="ml-1 text-rose-600 hover:text-rose-700"
                          data-testid={`live-pass-delete-${u.user_id}`}
                          onClick={() => setBorrado({ user_id: u.user_id, name: u.name, keep: true })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!cobro} onOpenChange={(v) => !v && setCobro(null)}>
        <DialogContent data-testid="live-pass-payment-dialog">
          <DialogHeader>
            <DialogTitle>{t("lp_register_payment")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {t("lp_payment_hint", { name: cobro?.name || "" })}
          </p>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div>
              <label className="text-xs font-medium text-slate-600">{t("paid_date")}</label>
              <Input type="date" value={cobro?.paid_date || ""} max={hoyISO}
                     data-testid="live-pass-paid-date"
                     onChange={(ev) => setCobro({ ...cobro, paid_date: ev.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t("payment_method")}</label>
              <Select value={cobro?.payment_method}
                      onValueChange={(v) => setCobro({ ...cobro, payment_method: v })}>
                <SelectTrigger className="mt-0" data-testid="live-pass-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t("cash")}</SelectItem>
                  <SelectItem value="deposit">{t("deposit")}</SelectItem>
                  <SelectItem value="card">{t("card")}</SelectItem>
                  <SelectItem value="unspecified">{t("unspecified")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCobro(null)}>{t("cancel")}</Button>
            <Button onClick={registrarPago} data-testid="live-pass-payment-confirm">
              {t("lp_register_payment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!borrado} onOpenChange={(v) => !v && setBorrado(null)}>
        <DialogContent data-testid="live-pass-delete-dialog">
          <DialogHeader>
            <DialogTitle>{t("lp_delete_title", { name: borrado?.name || "" })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{t("lp_delete_hint")}</p>
          <label className="flex items-start gap-2 text-sm text-slate-700 mt-1">
            <input
              type="checkbox" className="mt-1" checked={!!borrado?.keep}
              data-testid="live-pass-keep-history"
              onChange={(ev) => setBorrado({ ...borrado, keep: ev.target.checked })}
            />
            <span>{t("lp_keep_history")}</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBorrado(null)}>{t("cancel")}</Button>
            <Button variant="destructive" onClick={borrar} data-testid="live-pass-delete-confirm">
              {t("delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
